import {StyleSheet, View, Text, TouchableOpacity, Image} from "react-native";
import {
    BORDER_COLOR,
    CONNECTED_COLOR,
    DATA_COLOR,
    NOT_CONNECTED_COLOR,
    PRIMARY_BACKGROUND_COLOR
} from "../../Config/Colors";
import {DATA_FONT_SIZE, DATA_GRID_BORDER_WIDTH, DATA_GRID_HEIGHT} from "../../Config/Constants";
import PowerClient from "../../Domain/Models/PowerClient";
import Checkbox from 'expo-checkbox';
import React, {useEffect, useState} from "react";
import EditIcon from '../../Images/Icons/edit.png';

/**
 * Data Grid Item Props.
 */
interface DataGridItemProps
{
    /**
     * The power client.
     */
    powerClient: PowerClient;

    /**
     * Is Selected flag.
     */
    isSelected: boolean;

    /**
     * Callback when the checkbox was selected.
     * @param state The current checkbox state.
     * @param id The power client id.
     */
    onSelected?: (state: boolean, id: string) => void;

    /**
     * Callback when the edit button was selected.
     * @param powerClient The power client.
     */
    onEditButtonSelected?: (powerClient: PowerClient) => void;
}

/**
 * Renders the Data Grid Item.
 * @param powerClient The power client.
 * @param isSelected Is Selected flag.
 * @param onSelected Callback when the checkbox was selected.
 * @param onEditButtonSelected Callback when the edit button was selected.
 * @constructor
 */
export default function DataGridItem({
    powerClient,
    isSelected,
    onSelected,
    onEditButtonSelected,
}: DataGridItemProps)
{
    const [isChecked, setChecked] = useState(false);
    
    useEffect(() =>
    {
        let isMounted = true;
        
        if (powerClient.isDummy)
        {
            return;
        }
        
        if (isMounted)
        {
            setChecked(isSelected);
        }
        
        return () => { isMounted = false };
    }, [isSelected]);
    
    const onCheckboxValueChanged = (state: boolean): void =>
    {
        setChecked(state);
        
        if (onSelected !== undefined)
        {
            onSelected(state, powerClient.id);
        }
    }
    
    const getProxyName = (): string =>
    {
        if (powerClient.isDummy)
        {
            return "";    
        }
        
        if (powerClient.powerProxy === null || powerClient.powerProxy === undefined)
        {
            return "N/A";
        }
        
        return powerClient.powerProxy.name;
    }
    
    const getConnectionStatus = (): string =>
    {
        if (powerClient.isDummy)
        {
            return "";
        }
        
        return powerClient.isConnected ? "Connected" : "Not Connected";
    }
    
    const getConnectionColor = (): string =>
    {
        return powerClient.isConnected ? CONNECTED_COLOR: NOT_CONNECTED_COLOR;
    }
    
    const onEditPressed = (): void =>
    {
        if (onEditButtonSelected !== undefined)
        {
            onEditButtonSelected(powerClient);
        }
    }
    
    return (
        <View style={[styles.container, {
            opacity: powerClient.isDummy ? 0.5 : 1,
        }]}>
            <View style={[styles.item, {
                flex: 0.17,
            }]}>
                <Checkbox
                    style={styles.checkBox}
                    value={isChecked}
                    onValueChange={onCheckboxValueChanged}
                    disabled={powerClient.isDummy}
                />
            </View>
            
            <View style={styles.item}>
                <Text
                    numberOfLines={2}
                    style={[styles.text, {
                        color: getConnectionColor(),
                    }
                ]}>
                    {powerClient.name}
                </Text>
            </View>

            <View style={styles.item}>
                <Text
                    numberOfLines={2}
                    style={[styles.text, {
                        color: getConnectionColor(),
                    }
                ]}>
                    {getConnectionStatus()}
                </Text>
            </View>

            <View style={styles.item}>
                <Text
                    numberOfLines={2}
                    style={[styles.text, {
                        color: getConnectionColor(),
                    }
                ]}>
                    {getProxyName()}
                </Text>
            </View>

            <View style={[styles.item, {
                flex: 0.17,
            }]}>
                <TouchableOpacity
                    style={[styles.editButton, {
                        opacity: powerClient.isDummy ? 0 : 0.8,
                    }]}
                    disabled={powerClient.isDummy}
                    onPress={onEditPressed}
                >
                    <Image
                        source={EditIcon}
                        resizeMode={"contain"}
                        style={styles.icon}
                    />
                </TouchableOpacity>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        backgroundColor: PRIMARY_BACKGROUND_COLOR,
        width: "100%",
        height: DATA_GRID_HEIGHT,
        alignItems: 'center',
        flexDirection: 'row',
    },
    checkBox: {
        width: '55%',
        height: undefined,
        aspectRatio: 1,
    },
    editButton: {
        width: '50%',
        height: undefined,
        aspectRatio: 1,
        opacity: 0.7,
    },
    item: {
        flex: 1,
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        borderColor: BORDER_COLOR,
        borderWidth: DATA_GRID_BORDER_WIDTH,
        borderTopColor: 'transparent',
        borderTopWidth: 0,
    },
    text: {
        alignSelf: 'flex-start',
        textAlign: 'left',
        fontSize: DATA_FONT_SIZE,
        color: DATA_COLOR,
        width: '90%',
        paddingLeft: '5%',
        fontWeight: '600',
    },
    icon: {
        width: undefined,
        height: '100%',
        aspectRatio: 1,
    },
});
